import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../config/firebase';

const LETTERS_COLLECTION = 'letters';

/**
 * Investor letters / reports. PDFs live in Firebase Storage; metadata
 * (title, date, type, download url, storage path) lives in Firestore.
 * Shape: { title, date: 'YYYY-MM-DD', type, url, storagePath }
 */
export const lettersService = {
  /** Subscribe to all letters, newest first. */
  subscribeToLetters: (callback, errorCallback) => {
    const q = query(collection(db, LETTERS_COLLECTION), orderBy('date', 'desc'));
    return onSnapshot(
      q,
      (snapshot) => callback(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))),
      errorCallback
    );
  },

  /** One-time read of all letters. */
  getAll: async () => {
    const q = query(collection(db, LETTERS_COLLECTION), orderBy('date', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  /**
   * Upload a PDF to Storage and create the letter metadata doc.
   * @param {File} file - The PDF file to upload
   * @param {{title:string,date:string,type:string}} meta
   */
  uploadLetter: async (file, meta) => {
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const storagePath = `letters/${Date.now()}_${safeName}`;
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);

    const docRef = await addDoc(collection(db, LETTERS_COLLECTION), {
      title: meta.title || file.name,
      date: meta.date || '',
      type: meta.type || 'Quarterly',
      url,
      storagePath,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return docRef.id;
  },

  /**
   * Add a letter that points at an existing URL (no upload) - used for
   * seeding the historical PDFs already served from /public/letters.
   */
  addLetterByUrl: async ({ title, date, type, url }) => {
    const docRef = await addDoc(collection(db, LETTERS_COLLECTION), {
      title,
      date: date || '',
      type: type || 'Quarterly',
      url,
      storagePath: '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return docRef.id;
  },

  /** Update letter metadata (title / date / type). */
  updateLetter: async (id, meta) => {
    await updateDoc(doc(db, LETTERS_COLLECTION, id), {
      ...meta,
      updatedAt: serverTimestamp(),
    });
  },

  /** Delete a letter and its underlying Storage object (if any). */
  deleteLetter: async (letter) => {
    if (letter.storagePath) {
      try {
        await deleteObject(ref(storage, letter.storagePath));
      } catch (err) {
        // File may already be gone; proceed with metadata deletion.
        console.warn('Could not delete storage object:', err?.message);
      }
    }
    await deleteDoc(doc(db, LETTERS_COLLECTION, letter.id));
  },
};
